import { forwardRef, PropsWithChildren } from 'react';

import { mapClassNamesToSlots, TVStyleProps } from '../../../style-system';
import { FlexBox } from '../../flex-box';
import { ScrollArea } from '../../scroll-area/scroll-area';

import { containerStyles, rootStyles, sidebarStyles, topbarStyles, topnavStyles } from './content-layout.styles';

/** Wrapper for all content within a page */
export const ContentLayout = ({ children, collapsed }: PropsWithChildren<{ collapsed?: boolean }>) => {
    return <div className={rootStyles({ collapsed })}>{children}</div>;
};

/** Content container for a sticky top bar; Displayed on all screensizes */
const Topbar = ({ children }: PropsWithChildren) => {
    return <div className={topbarStyles()}>{children}</div>;
};

/** A scrollable container for a sticky top nav; Displayed on screen sizes `<768px` (`md`) */
const Topnav = ({ children, showOnAllBreakpoints = false }: PropsWithChildren<TVStyleProps<typeof topnavStyles>>) => {
    const { base, innerWrapper } = topnavStyles({ showOnAllBreakpoints });
    return (
        <div className={base()}>
            <div className={innerWrapper()}>
                <ScrollArea h="full" maxW="full" thumb="none">
                    <FlexBox px={3} py={2}>
                        {children}
                    </FlexBox>
                </ScrollArea>
            </div>
        </div>
    );
};

type SidebarProps = PropsWithChildren<TVStyleProps<typeof sidebarStyles>>;

/** Content container for a top nav; Displayed on screen sizes `>768px` (`md`) */
const Sidebar = ({ children, className, fullHeight, noPaddingY }: SidebarProps) => {
    return (
        <div className={sidebarStyles({ className, fullHeight, noPaddingY })}>
            <FlexBox col minH="full">
                {children}
            </FlexBox>
        </div>
    );
};

const ScrollableSidebar = ({ children, fullHeight, ...rest }: SidebarProps) => {
    return (
        <div
            className={sidebarStyles({ noPaddingY: true, fullHeight, ...rest })}
            style={
                {
                    ['--sidebar-offset']: fullHeight ? 'var(--navbar-height)' : 'var(--content-offset)',
                } as React.CSSProperties
            }
        >
            <ScrollArea h="calc(100vh - var(--sidebar-offset))" maxW="full" thumb="none">
                <FlexBox col minH="full">
                    {children}
                </FlexBox>
            </ScrollArea>
        </div>
    );
};

Sidebar.Scrollable = ScrollableSidebar;

/** An optionally scrollable container for the body content of a layout */
const Container = forwardRef<
    HTMLDivElement,
    PropsWithChildren<{ custom?: boolean } & TVStyleProps<typeof containerStyles>>
>(function Container({ children, custom = false, ...rest }, scrollAreaRef) {
    const { base, innerContainer } = mapClassNamesToSlots(containerStyles, rest);
    return (
        <div className={base}>
            {!custom && (
                <ScrollArea ref={scrollAreaRef} h="calc(100vh - var(--content-offset))" fitContainer w="full">
                    <div className="print:absolute print:min-h-max">
                        <div className={innerContainer}>{children}</div>
                    </div>
                </ScrollArea>
            )}
            {custom && children}
        </div>
    );
});

ContentLayout.Topbar = Topbar;

ContentLayout.Sidebar = Sidebar;

ContentLayout.Container = Container;

ContentLayout.Topnav = Topnav;
